import { useEffect, useRef } from 'react';
import { type Control, type UseFormSetValue, useWatch } from 'react-hook-form';

import type { Order } from '~hooks/useOrders/models';
import { Phase } from '~hooks/useProjects/models';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * The embedded ProjectDetailsV2 → PhaseSelection drawer writes `phases` with a
 * bare `setValue('phases', …)` — no `{ shouldDirty: true }`. `phases` is a
 * runtime-extra form value, not a yup schema key, so its add / edit / delete /
 * set-primary edits never enter RHF's `dirtyFields`. deparseUpdateV3 gates on
 * `dirtyFields`, so an edit-only change produced an EMPTY PATCH and
 * OrderForm.tsx skipped the save entirely (TRE-15128). This module re-stamps
 * the field dirty the moment its content actually changes.
 *
 * The signature is SEMANTIC, not by reference: PhaseSelection echoes the seed
 * back as reshaped plain option objects on mount (same name/code/primary, new
 * references), and that echo must NOT dirty an untouched form, or every
 * edit-form open would send `phases` and trip the dirty/unsaved indicator.
 * The baseline starts at the seed; only a real edit flips it. `_destroy` is
 * part of the signature because deleting a phase only sets `_destroy: 1` on
 * an in-place row rather than removing it from the array.
 *
 * Unlike the cascade effects (whose internal writes use `{ shouldDirty: false }`),
 * this write is the deliberate propagation of a genuine USER edit the embed
 * failed to flag — so it dirties, mirroring `onDefaultSiteRateScheduleChange`.
 *
 * Triggers: the form `phases` value (watched).
 * Writes:   re-applies the current `phases` with `{ shouldDirty: true }` once
 *           its semantic content diverges from the seed.
 * Guards:   semantic signature vs. the seed baseline — the mount echo is a
 *           no-op; only a genuine add/edit/delete/primary change dirties.
 * Legacy:   the legacy form sends `phases` on every update unconditionally;
 *           v3 gates it on dirty like every field.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("phases dirty tracking").
 */
function phasesSignature(phases: readonly Phase[] | null | undefined): string {
  return (phases ?? [])
    .map((phase) => {
      // Phase.deparseUpdate is the canonical projection (the same one the
      // update payload uses): it reads name/code/primary/_destroy from inside
      // the class, and the form's plain option objects carry those very keys
      // at runtime, so it normalizes seed instances and edited plain rows alike.
      const projected = Phase.deparseUpdate(phase);

      return [
        projected.name,
        projected.code ?? '',
        projected.primary ? 1 : 0,
        projected._destroy ? 1 : 0,
      ].join('|');
    })
    .join(';');
}

export function usePhasesDirtySync({
  control,
  defaultOrder,
  setValue,
}: {
  control: Control<OrderFormV3FieldValues>;
  defaultOrder: Order | null;
  setValue: UseFormSetValue<OrderFormV3FieldValues>;
}): void {
  const phases = useWatch({ control, name: 'phases' });
  const signature = phasesSignature(phases);
  const previousSignatureRef = useRef(phasesSignature(defaultOrder?.phases));

  useEffect(() => {
    if (signature === previousSignatureRef.current) {
      return;
    }
    previousSignatureRef.current = signature;
    setValue('phases', phases, { shouldDirty: true });
  }, [signature, phases, setValue]);
}
